/**
 * Ein Mikrofon an ein Textfeld. (08.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Wenn ich bei Absicht auf der Startseite mit dem Cursor
 * reingehe, poppt zwar die Tastatur auf, aber ich kann nicht reinsprechen.
 * Bitte mit Mikrofon auch einsprechbar machen, so wie bei anderen Elementen
 * schon."*
 *
 * Er hat recht, und die Betonung liegt auf „so wie bei anderen": Den Knopf
 * gab es schon — in `file-caption-modal.ts`, vierzig Zeilen, die man beim
 * dritten Feld ein drittes Mal abgeschrieben haette. Deshalb steht er
 * jetzt hier, EINMAL, und beide Felder holen ihn sich.
 *
 * ## Die Regeln, die dabei gelten
 *
 * ⚠️ **Ein Mikrofon zur Zeit.** `LiveDictation` oeffnet den Zugang; ein
 *    zweites `getUserMedia` daneben ist in diesem Haus schon mehrfach
 *    schiefgegangen. Wer tippt, beendet das Diktat — die eigene Eingabe hat
 *    Vorrang.
 *
 * ⚠️ **Der Text waechst, er ersetzt nicht.** Was vor dem Sprechen im Feld
 *    stand, bleibt stehen; das Gesprochene kommt dahinter. Sonst loescht
 *    ein Fehlgriff den halben Satz.
 */
import { LiveDictation } from "./live-dictation.js";
import { mikrofonZeichen } from "./mikro-zeichen.js";

interface MikrofonOptionen {
  /** Klasse fuer den Knopf; sonst die geteilte `.feld-mikro`. */
  klasse?: string;
  /** Nach jedem Textzuwachs — etwa zum Nachwachsen des Feldes. */
  beiText?: () => void;
}

interface FeldMikrofon {
  knopf: HTMLButtonElement;
  /** Laeuft gerade ein Diktat? */
  laeuft: () => boolean;
  /** Von aussen beenden (Blatt schliesst, Feld verschwindet). */
  beenden: () => void;
}

/**
 * Haengt ein Mikrofon an ein Feld und gibt den Knopf zurueck. Der Aufrufer
 * entscheidet, wo er steht.
 */
export function haengeMikrofonAn(
  feld: HTMLTextAreaElement | HTMLInputElement,
  opts: MikrofonOptionen = {},
): FeldMikrofon {
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = opts.klasse ?? "feld-mikro";
  knopf.setAttribute("aria-label", "Diktieren");
  knopf.title = "Diktieren";
  knopf.appendChild(mikrofonZeichen());

  let diktat: { stop: () => void } | null = null;
  let basis = "";

  const beenden = (): void => {
    diktat?.stop();
    diktat = null;
    knopf.removeAttribute("data-state");
    knopf.setAttribute("aria-label", "Diktieren");
  };

  knopf.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (diktat) {
      beenden();
      return;
    }
    basis = feld.value.trim();
    knopf.setAttribute("data-state", "recording");
    knopf.setAttribute("aria-label", "Diktat beenden");
    void (async () => {
      try {
        const d = new LiveDictation({
          onText: (fin, interim) => {
            const t = (fin + (interim ? " " + interim : "")).trim();
            if (!t) return;
            feld.value = basis ? `${basis} ${t}` : t;
            opts.beiText?.();
          },
          onError: () => beenden(),
          onFinished: () => beenden(),
        });
        await d.start();
        diktat = d;
      } catch {
        beenden();
      }
    })();
  });

  // Eigene Eingabe hat Vorrang — dieselbe Regel wie im Hauptfeld.
  feld.addEventListener("keydown", () => {
    if (diktat) beenden();
  });

  return { knopf, laeuft: () => diktat !== null, beenden };
}
